import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import LoadingSpinner from './LoadingSpinner';

const CUALgiftcardComponent = ({ user }) => {
  const [tab, setTab] = useState('buy'); // buy | redeem | mine
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [issued, setIssued] = useState([]);
  const [received, setReceived] = useState([]);

  // Buy form
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [buying, setBuying] = useState(false);

  // Redeem form
  const [code, setCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemResult, setRedeemResult] = useState(null);

  // Recently issued (after buy)
  const [recentlyIssued, setRecentlyIssued] = useState(null);

  const fetchLevels = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/giftcards/levels`);
      setLevels(res.data.levels || []);
    } catch (e) {
      console.error('levels error:', e);
    }
  }, []);

  const fetchMine = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/giftcards/mine`);
      setIssued(res.data.issued || []);
      setReceived(res.data.received || []);
    } catch (e) {
      console.error('mine error:', e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchLevels(), fetchMine()]);
      setLoading(false);
    })();
  }, [fetchLevels, fetchMine]);

  // Capture giftcard purchase confirmation from URL (after Stripe success)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('giftcard') === 'success' && params.get('session_id')) {
      const sid = params.get('session_id');
      (async () => {
        try {
          const res = await axios.get(`${API}/giftcards/payment/status/${sid}`);
          if (res.data.status === 'paid' && res.data.giftcard) {
            setRecentlyIssued(res.data.giftcard);
            setTab('mine');
            await fetchMine();
          }
        } catch (e) {
          console.error('giftcard verify error', e);
        } finally {
          // Clear URL params
          window.history.replaceState({}, '', window.location.pathname);
        }
      })();
    }
  }, [fetchMine]);

  const startCheckout = async () => {
    if (!selectedLevel) return;
    setBuying(true);
    try {
      const origin = window.location.origin + window.location.pathname;
      const res = await axios.post(`${API}/giftcards/checkout`, {
        level: selectedLevel,
        recipient_email: recipientEmail.trim() || null,
        message: giftMessage.trim() || null,
        origin_url: origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Error iniciando compra');
    } finally {
      setBuying(false);
    }
  };

  const validateCode = async (rawCode) => {
    const c = rawCode.trim().toUpperCase();
    if (c.length < 12) {
      setValidation(null);
      return;
    }
    setValidating(true);
    try {
      const res = await axios.get(`${API}/giftcards/check/${encodeURIComponent(c)}`);
      setValidation(res.data);
    } catch (e) {
      setValidation({ valid: false, message: e.response?.data?.detail || 'Codigo invalido' });
    } finally {
      setValidating(false);
    }
  };

  const redeem = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    setRedeemResult(null);
    try {
      const res = await axios.post(`${API}/giftcards/redeem`, { code: code.trim().toUpperCase() });
      setRedeemResult(res.data);
      setCode('');
      setValidation(null);
      await fetchMine();
    } catch (e) {
      setRedeemResult({ success: false, message: e.response?.data?.detail || 'Error al canjear' });
    } finally {
      setRedeeming(false);
    }
  };

  const copyCode = (c) => {
    navigator.clipboard.writeText(c);
    alert('Codigo copiado al portapapeles');
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6" data-testid="cualgiftcard-component">
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-500 via-pink-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
        <h2 className="text-3xl font-bold flex items-center gap-3" data-testid="giftcard-title">
          <span>CUALgiftcard</span>
        </h2>
        <p className="mt-2 text-white/90 text-sm max-w-2xl">
          Regala el ecosistema CUALAPP. Cada giftcard incluye un WEN/Celula activo + Cc respaldados.
          El receptor canjea el codigo y entra al sistema con su WEN listo.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {[
          { id: 'buy', label: 'Comprar' },
          { id: 'redeem', label: 'Canjear codigo' },
          { id: 'mine', label: 'Mis giftcards' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`giftcard-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* BUY */}
      {tab === 'buy' && (
        <div className="space-y-4" data-testid="giftcard-buy-panel">
          <h3 className="text-lg font-semibold text-gray-800">Elige el nivel</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {levels.map((lvl) => (
              <button
                key={lvl.id}
                onClick={() => setSelectedLevel(lvl.id)}
                data-testid={`giftcard-level-${lvl.id}`}
                className={`text-left rounded-xl p-4 border-2 transition-all ${
                  selectedLevel === lvl.id
                    ? 'border-purple-600 bg-purple-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-purple-300'
                }`}
              >
                <p className="text-xs uppercase tracking-wide text-purple-600 font-semibold">{lvl.wen_capacity}</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{lvl.name}</p>
                <p className="text-3xl font-extrabold text-gray-900 mt-2">${lvl.price_usd}</p>
                <p className="text-sm text-gray-600 mt-1">+{lvl.cualcoin_included} Cc</p>
                <p className="text-xs text-gray-500 mt-2 leading-tight">{lvl.description}</p>
              </button>
            ))}
          </div>

          {selectedLevel && (
            <div className="bg-white rounded-xl p-5 border border-gray-200 space-y-3">
              <h4 className="font-semibold text-gray-800">Detalles del regalo (opcional)</h4>
              <div>
                <label className="text-xs text-gray-600">Email del receptor (referencia, opcional)</label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  data-testid="giftcard-recipient-email"
                  placeholder="amigo@correo.com"
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-300"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Mensaje (max 200 chars)</label>
                <textarea
                  value={giftMessage}
                  onChange={(e) => setGiftMessage(e.target.value.slice(0, 200))}
                  data-testid="giftcard-message"
                  placeholder="Feliz cumpleanos!"
                  rows={2}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-300"
                />
                <p className="text-xs text-gray-400 text-right mt-1">{giftMessage.length}/200</p>
              </div>
              <button
                onClick={startCheckout}
                disabled={buying}
                data-testid="giftcard-buy-btn"
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                {buying ? 'Iniciando...' : `Pagar $${levels.find((l) => l.id === selectedLevel)?.price_usd} con Stripe`}
              </button>
              <p className="text-xs text-gray-500 text-center">
                Pagas en USD. El receptor recibira el codigo para canjear su WEN + Cc.
              </p>
            </div>
          )}

          {recentlyIssued && (
            <div className="bg-green-50 border-2 border-green-300 rounded-xl p-5" data-testid="giftcard-success-banner">
              <h4 className="font-bold text-green-800">Tu CUALgiftcard se emitio</h4>
              <p className="text-sm text-green-700 mt-1">
                Comparte este codigo con el receptor. Puede canjearlo desde la pestana <b>Canjear codigo</b>.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-white rounded-lg border border-green-200 font-mono text-lg tracking-wider text-center" data-testid="recently-issued-code">
                  {recentlyIssued.code}
                </code>
                <button
                  onClick={() => copyCode(recentlyIssued.code)}
                  data-testid="copy-recently-issued"
                  className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm"
                >
                  Copiar
                </button>
              </div>
              <p className="text-xs text-green-700 mt-2">
                Nivel: {recentlyIssued.level_name} | +{recentlyIssued.cualcoin_included} Cc
              </p>
            </div>
          )}
        </div>
      )}

      {/* REDEEM */}
      {tab === 'redeem' && (
        <div className="space-y-4" data-testid="giftcard-redeem-panel">
          <div className="bg-white rounded-xl p-5 border border-gray-200 space-y-3">
            <h3 className="text-lg font-semibold text-gray-800">Ingresa el codigo de tu CUALgiftcard</h3>
            <input
              type="text"
              value={code}
              onChange={(e) => {
                const v = e.target.value.toUpperCase();
                setCode(v);
                validateCode(v);
              }}
              data-testid="giftcard-code-input"
              placeholder="XXXX-XXXX-XXXX-XXXX"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg font-mono tracking-widest text-center text-lg uppercase focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
            />

            {validating && <p className="text-xs text-gray-500">Validando...</p>}

            {validation && validation.valid && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid="validation-ok">
                <p className="text-sm font-semibold text-green-800">Codigo valido</p>
                <p className="text-xs text-green-700">
                  Nivel: <b>{validation.level_name}</b> | +{validation.cualcoin_included} Cc
                </p>
                {validation.message && (
                  <p className="text-xs italic text-green-600 mt-1">"{validation.message}"</p>
                )}
                <p className="text-xs text-green-700 mt-1">De: {validation.issuer_email}</p>
              </div>
            )}

            {validation && !validation.valid && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="validation-error">
                <p className="text-sm text-red-700">{validation.message}</p>
              </div>
            )}

            <button
              onClick={redeem}
              disabled={redeeming || !validation?.valid}
              data-testid="giftcard-redeem-btn"
              className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white py-3 rounded-lg font-semibold transition-colors"
            >
              {redeeming ? 'Canjeando...' : 'Canjear ahora'}
            </button>
          </div>

          {redeemResult && (
            <div
              className={`rounded-xl p-5 border-2 ${redeemResult.success ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}
              data-testid="redeem-result"
            >
              <p className={`font-bold ${redeemResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {redeemResult.success ? 'Canje exitoso' : 'No se pudo canjear'}
              </p>
              <p className={`text-sm mt-1 ${redeemResult.success ? 'text-green-700' : 'text-red-700'}`}>
                {redeemResult.message}
              </p>
              {redeemResult.success && (
                <p className="text-xs text-green-600 mt-2">
                  Recargada la pagina para ver tu nuevo WEN/Cc.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* MINE */}
      {tab === 'mine' && (
        <div className="space-y-6" data-testid="giftcard-mine-panel">
          <section>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Emitidas por mi ({issued.length})</h3>
            {issued.length === 0 ? (
              <p className="text-sm text-gray-500">No has emitido giftcards aun.</p>
            ) : (
              <div className="space-y-2">
                {issued.map((g) => (
                  <div key={g.id} className="bg-white rounded-lg p-4 border border-gray-200 flex items-center justify-between" data-testid={`issued-${g.id}`}>
                    <div className="min-w-0">
                      <p className="font-mono text-sm tracking-wider truncate">{g.code}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {g.level_name} | +{g.cualcoin_included} Cc | ${g.purchase_price_usd}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {g.status === 'redeemed' ? `Canjeada por ${g.recipient_email}` : 'Pendiente de canje'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          g.status === 'issued'
                            ? 'bg-blue-100 text-blue-700'
                            : g.status === 'redeemed'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {g.status}
                      </span>
                      {g.status === 'issued' && (
                        <button
                          onClick={() => copyCode(g.code)}
                          data-testid={`copy-${g.id}`}
                          className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                        >
                          Copiar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Recibidas ({received.length})</h3>
            {received.length === 0 ? (
              <p className="text-sm text-gray-500">No has canjeado ninguna giftcard aun.</p>
            ) : (
              <div className="space-y-2">
                {received.map((g) => (
                  <div key={g.id} className="bg-white rounded-lg p-4 border border-green-200" data-testid={`received-${g.id}`}>
                    <p className="text-sm font-medium text-gray-800">
                      {g.level_name} (+{g.cualcoin_included} Cc)
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">De: {g.issuer_email}</p>
                    {g.message && <p className="text-xs italic text-gray-600 mt-1">"{g.message}"</p>}
                    <p className="text-xs text-gray-400 mt-1">
                      Canjeada: {g.redeemed_at ? new Date(g.redeemed_at).toLocaleDateString('es-ES') : '-'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default CUALgiftcardComponent;
